import { Injectable, OnDestroy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, of, Subject, Subscription, TeardownLogic } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { addSeconds, differenceInSeconds, isValid } from 'date-fns';
import { SessionStorage } from 'ngx-webstorage';
import { ConfigService } from '../config/config.service';
import { UnsavedChangesService } from '../../routing/auth.guard';
import { UserService } from '../user/user.service';
import { BUILD_TIME, VERSION_NUMBER } from '../version/version.service';
import { RoutePath } from '../../routing/routes';
import { ConfirmationDialogOptions, ConfirmationDialogService } from 'app/shared/dialogs/confirmation';
import { CountdownDialogOptions, CountdownDialogService } from 'app/shared/dialogs/countdown';
import { MessageBoxDialogOptions, MessageBoxDialogService } from 'app/shared/dialogs/messagebox';
import { AuthTokenRefreshResult, TestConnectionPacket } from './auth';
import { UserInfo, UserRole } from '../user/user';
import { SplashDialogService } from 'app/shared/dialogs/splash';
import { ErrorService } from '../error/error.service';


@Injectable({
    providedIn: 'root',
})
export class AuthService implements OnDestroy {
    // 17 minutes (time period to wait between successive checks of whether the auth token is recently used)
    static AUTHCHECKTIME: number = 17 * 60;
    // 2 minutes, 50 seconds (time period to wait before logging the user out automatically). Roughly 20 minutes total between check
    // and autologout
    static LOGOUTWARNINGTIME = 170;

    private _subrelease: Subscription = new Subscription();
    set subrelease(sub: TeardownLogic) {
        this._subrelease.add(sub);
    }

    @SessionStorage(undefined, null) nextAuthRefresh!: string | null;
    @SessionStorage(undefined, false) private systemOnline = false;

    AuthTokenCancelFlag: boolean;
    AuthTokenRefreshHandle: number | null;
    AuthRefreshDeadline: Date | null;
    HasVersionUpdate: Subject<boolean> = new Subject<boolean>();
    SystemOnlineSubject: Subject<TestConnectionPacket> = new Subject<TestConnectionPacket>();
    User: UserInfo | null = null;
    VersionNumber: Subject<string> = new Subject<string>();
    IsRefreshing: boolean;

    constructor(
        private configService: ConfigService,
        private confirmationDialogService: ConfirmationDialogService,
        private countdownDialogService: CountdownDialogService,
        private errorService: ErrorService,
        private messageboxDialogService: MessageBoxDialogService,
        private datePipe: DatePipe,
        private http: HttpClient,
        private router: Router,
        private splashService: SplashDialogService,
        private unsavedChangesSvc: UnsavedChangesService,
        private userService: UserService,
    ) {
        this.AuthTokenCancelFlag = false;
        this.AuthTokenRefreshHandle = null;
        this.AuthRefreshDeadline = this.nextAuthRefresh ? new Date(this.nextAuthRefresh) : null;
        this.IsRefreshing = false;

        const btime = new Date(BUILD_TIME);

        this.VersionNumber.next(VERSION_NUMBER);

        this.SystemOnlineSubject.next({
            SystemMessage: '',
            Status: this.systemOnline,
            PathwaysVersion: VERSION_NUMBER,
        });

        // Set up the subscription to watch for the user being updated, so we can tell if we need to boot up auth token refreshing.
        this.subrelease = this.userService.UserSubject.subscribe((user) => {
            this.User = user;

            // See if we have a User object already known to us. If so, this was a refresh of the page most likely,
            // and thus we need to kick start authtoken refreshing again.
            if (this.User?.AuthToken && !this.IsRefreshing) {
                this.StartAuthTokenRefreshing();
            }
        });
    }

    ngOnDestroy(): void {
        this.CancelAuthTokenRefreshing();
        this._subrelease.unsubscribe();
    }

    // #region Auth Token Refresh Method

    private AuthTokenRefresh() {
        const self = this;

        // Now retrieve the auth token for the currently logged in user, stored in the userService
        const auth = self.User?.AuthToken;

        // Send a request to the system to check to see if the user's "Recent Use" flag is set. If it is, we will refresh their
        // auth token and reset all countdown timers.
        this.CheckRecentUse(auth).subscribe({
            next: (recentUse) => {
                // If we get a non-null result, it indicates the status of the "Recent Use" flag. If it's true, we trigger an
                // auth token refresh, which in turn will reset the clock for our next check of recent use, etc.
                if (recentUse === true) {
                    // Call the service which will refresh the auth token.
                    this.RefreshAuthToken().subscribe({
                        next: (newAuthPacket) => {
                            const newAuth = (newAuthPacket && newAuthPacket.AuthToken) || newAuthPacket;

                            if (newAuthPacket && newAuthPacket.PathwaysVersion) {
                                // We have version information. Does it match the version known here in the TypeScript?
                                // If not, set the update flag.
                                if (VERSION_NUMBER !== newAuthPacket.PathwaysVersion) {
                                    // Mismatch. Set the flag.
                                    this.HasVersionUpdate.next(true);
                                }
                            }

                            // We have a valid, new auth token. Update the User variable in the userService to reflect this.
                            this.userService.UpdateAuthToken(newAuth);

                            // If there is no flag signal set preventing this loop from continuing, then we can set up the
                            // next iteration with a timeout now.
                            if (!this.AuthTokenCancelFlag) {
                                this.AuthTokenRefreshHandle = window.setTimeout(() => {
                                    this.AuthTokenRefresh();
                                }, AuthService.AUTHCHECKTIME * 1000);

                                // Set the refresh deadline app value
                                const Now = new Date();
                                this.AuthRefreshDeadline = addSeconds(Now, AuthService.AUTHCHECKTIME);
                                this.nextAuthRefresh = this.AuthRefreshDeadline.toISOString();
                                // this.nextAuthRefresh = this.toolsService.toISOLocal(this.AuthRefreshDeadline);
                            }
                        },
                        error: (error) => {
                            // An error occurred. Attempt to retrieve it.
                            let body = error.json();
                            body =
                                (body && body.Message) ||
                                body ||
                                'An error occurred while trying to valid your active connection to the system. You have been logged out.';
                            
                                this.errorService.HandleServiceError(error);
                                console.error(error);
                        }
                    });
                } else {
                    // We either could not retrieve the "Recent Use" flag, or we DID and it was false. In either case, we must begin a
                    // countdown process to log out the user from the system.
                    // First, disable the current auth token refresh cycling. We won't restart that until we get feedback from the user.
                    this.CancelAuthTokenRefreshing();

                    const options: CountdownDialogOptions = <CountdownDialogOptions>{
                        TitleText: 'Session Inactivity Logout',
                        MessageText:
                            `You are about to be automatically logged out of the system due to inactivity. If you wish to ` +
                            `stay logged in to the site, please click the "Stay Logged In" button below.`,
                        OKText: 'Stay Logged In',
                        CancelText: 'Log Out',
                        CountdownTime: AuthService.LOGOUTWARNINGTIME,
                    };

                    this.countdownDialogService.Open(options).subscribe((res) => {
                        if (res === true) {
                            // User wants to stay logged in - Restart the auth token refresh cycling.
                            this.RefreshAuthToken().subscribe(
                                (newAuthPacket) => {
                                    const newAuth = (newAuthPacket && newAuthPacket.AuthToken) || newAuthPacket;

                                    if (newAuthPacket && newAuthPacket.PathwaysVersion) {
                                        // We have version information. Does it match the version known here in the TypeScript?
                                        // If not, set the update flag.
                                        if (VERSION_NUMBER !== newAuthPacket.PathwaysVersion) {
                                            // Mismatch. Set the flag.
                                            this.HasVersionUpdate.next(true);
                                        }
                                    }

                                    // We have a new valid auth token to use. Update the user object accordingly with the new token.
                                    this.userService.UpdateAuthToken(newAuth);

                                    // If there is no flag signal set preventing this loop from continuing, then we can set up the
                                    // next iteration with a timeout now.
                                    if (!this.AuthTokenCancelFlag) {
                                        this.AuthTokenRefreshHandle = window.setTimeout(() => {
                                            this.AuthTokenRefresh();
                                        }, AuthService.AUTHCHECKTIME * 1000);

                                        // Set the refresh deadline app value
                                        const Now = new Date();
                                        this.AuthRefreshDeadline = addSeconds(Now, AuthService.AUTHCHECKTIME);
                                        this.nextAuthRefresh = this.AuthRefreshDeadline.toISOString();

                                        // this.nextAuthRefresh = this.toolsService.toISOLocal(this.AuthRefreshDeadline);
                                    }
                                },
                                (error) => {
                                    // Snackbar the error
                                    let body = error.json();
                                    body =
                                        (body && body.Message) ||
                                        body ||
                                        `An error occurred while trying to valid your active connection to the system. ` +
                                            `You have been logged out.`;
                                    this.splashService.SnackbarError(body, {
                                        duration: 5000,
                                    });
                                }
                            );
                        } else {
                            // User wishes to logout, or the timer expired. Either way, log them out.
                            this.Logout(false, 1);
                        }
                    });
                }
            },
            error: (error) => {
                // This represents an error when trying to get recent use. Likely this is due to the session expiring in the
                // authtoken. In this case, cancel the refresh handle, and log out the user.
                this.CancelAuthTokenRefreshing();

                this.Logout(false, 2);
                this.errorService.HandleServiceError(error);
                console.error(error);
            }
        });
    }

    // #endregion

    // #region Auth Token Refresh Method v2

    private AuthTokenRefreshv2(): void {
        const self = this;

        // Now retrieve the auth token for the currently logged in user, stored in the userService
        const auth = self.User?.AuthToken;

        // Call the system refresh routine.
        self.RefreshAuthToken().subscribe({
            next: (authresult) => {
                // If no token comes up, the user got logged out somehow.
                if (!authresult || !authresult.AuthToken) {
                    self.messageboxDialogService.Open({
                            TitleText: 'Connection Lost',
                            MessageText:
                                'Your connection to the system has been broken. You will need to login again to use the system.',
                            OKText: 'OK',
                        } as MessageBoxDialogOptions)
                        .subscribe((_) => {
                            self.Logout();

                            // Redirect to login.
                            self.router.navigate([RoutePath.Login]);
                        });
                } else {
                    // Update their token.
                    self.userService.UpdateAuthToken(authresult.AuthToken);

                    // Set the session variable as well.
                    self.AuthRefreshDeadline = addSeconds(new Date(), AuthService.AUTHCHECKTIME);
                    // self.nextAuthRefresh = self.toolsService.toISOLocal(self.AuthRefreshDeadline);
                    self.nextAuthRefresh = self.AuthRefreshDeadline.toISOString();

                    // Reset for the next auth refresh cycle.
                    self.AuthTokenRefreshHandle = window.setTimeout(() => {
                        self.AuthTokenRefresh();
                    }, AuthService.AUTHCHECKTIME * 1000);
                }
            },
            error: (error) => {
                this.errorService.HandleServiceError(error);
                console.error(error);
            },
        });
    }

    // #endregion

    // #region Cancel Auth Token Refreshing Method

    CancelAuthTokenRefreshing(): void {
        // If possible, use the handle to cancel the timeout from happening
        if (this.AuthTokenRefreshHandle != null) {
            window.clearTimeout(this.AuthTokenRefreshHandle);
        }

        // Cancel the flag, handle, and deadline to refresh
        this.AuthRefreshDeadline = null;
        this.AuthTokenRefreshHandle = null;
        this.AuthTokenCancelFlag = false;
        this.IsRefreshing = false;
        this.nextAuthRefresh = null;
    }

    // #endregion

    // #region Change Impersonation Target Service Call

    ChangeImpersonationTarget(AuthToken: string, SAPID: string) {
        // return this.http.get<LoginResult>(
        return this.http.get<UserInfo>( //TODO: Check LoginResult 
            `${this.configService.hostAddress}` +
                `Api/Services/Authorization/ChangeImpersonationTarget?AuthToken=${AuthToken || ''}&SAPID=${SAPID || ''}`
        );
    }

    // #endregion

    // #region Check Recent Use Service Method - This method simply calls the database to see if the user has done anything

    CheckRecentUse(AuthToken?: string | null): Observable<boolean> {
        return this.http.get<boolean>(
            this.configService.hostAddress + `Api/Services/Authentication/CheckRecentUse?AuthToken=${AuthToken || ''}`
        );
    }

    // #endregion


    // #region Handle Service Error Service Call

    // tslint:disable-next-line: no-any
    HandleServiceError(error: any): void {
        let status: number | null = null;
        let msg = '';

        if (error instanceof HttpErrorResponse) {
            status = error.status;

            const body = error && error.error;
            msg = body.ExceptionMessage || body.Message || body || 'An unknown error has occurred.';
        } else {
            let errorPacket = error.message || error;
            if (errorPacket != null && errorPacket !== '') {
                try {
                    errorPacket = JSON.parse(errorPacket);
                } catch {
                    errorPacket = errorPacket;
                }
            }
            msg =
                errorPacket != null && errorPacket instanceof Object
                    ? errorPacket.ExceptionMessage || errorPacket.Message || error
                    : errorPacket || 'An unknown error has occurred.';
        }

        this.splashService.CloseSplash();

        // Something else, which should be snackbar displayed.
        this.splashService.SnackbarError(msg, {
            duration: 8000,
        });
    }

    // #endregion

    // #region Logout From System Service Call

    LogoutFromSystem(): Observable<boolean> {
        return this.http.put<boolean>(this.configService.hostAddress + `Api/Services/Authentication/Logout`, {});
    }

    // #endregion

    // #region Logout Service Call

    Logout(confirmation?: boolean, reason?: number): void {
        const self = this;
        let confObs: Observable<boolean>;

        if (confirmation === true) {
            // Pop up a confirmation box to confirm desire to logout.
            const opts = {
                TitleText: 'Confirmation Requested',
                MessageText: 'You are about to log out of the site. Are you sure you wish to exit at this time?',
                OKText: 'Yes - Log Out',
                CancelText: 'No - Stay Logged In',
            };

            confObs = self.confirmationDialogService.Open(opts as ConfirmationDialogOptions);
        } else {
            // No dialog needed.
            confObs = of<boolean>(true).pipe(take(1));
        }

        confObs.subscribe((result) => {
            if (result === true) {
                // User is opting to log out. Proceed.
                self.LogoutFromSystem().subscribe();

                // Stop authtoken refreshing
                self.CancelAuthTokenRefreshing();

                // Null out the current user.
                self.userService.ClearUser();

                // Snackbar the logout if this was user initiated (reason null or 0)
                if (reason == null || reason === 0) {
                    self.splashService.SnackbarSuccess('Successfully logged out.', {
                        duration: 5000,
                    });
                }

                // Close out all remaining open dialogs
                // self.matDialog.closeAll();
                self.confirmationDialogService.CloseAllDialogs();

                // Clear out change tracking.
                self.unsavedChangesSvc.ClearTracking();

                // Navigate to the login state.
                self.router.navigate([RoutePath.Login]);
                // window.location.href = self.configService.hostAddress;
            }
        });
    }

    // #endregion

    // #region Process Login

    // ProcessLogin(logininfo: LoginResult): void {
    ProcessLogin(user: UserInfo, FromSSO?: boolean) {
        const self = this;

        // Convert the LoginResult object to a UserInfo object.
        // const userinfo = self.ConvertToUserInfo(logininfo);

        // Set the currently logged in user information.
        self.userService.SetUser(user);

        // Set the current role
        let topRole: UserRole | null = null;
        if (user && user.Roles && !!user.Roles.length) topRole = user.Roles[0];

        if (user?.Roles?.length) {
            // self.userService.SetUserCurrentRole(userinfo.Roles[0]);

            if (topRole?.IsDeveloper) this.userService.SetUserCurrentRole(topRole, 'Developer');
            else if (topRole?.IsSystemAdmin) this.userService.SetUserCurrentRole(topRole, 'SystemAdmin');
            else if (topRole?.IsClearinghouse) this.userService.SetUserCurrentRole(topRole, 'Clearinghouse');
            else if (topRole?.IsProgramContact) this.userService.SetUserCurrentRole(topRole, 'ProgramContact');
            else if (topRole?.IsDistrictStaff) this.userService.SetUserCurrentRole(topRole, 'DistrictStaff');
            else if (topRole?.IsPrincipal) this.userService.SetUserCurrentRole(topRole, 'Principal');
            else if (topRole?.IsAssistantPrincipal) this.userService.SetUserCurrentRole(topRole, 'AssistantPrin');
            else if (topRole?.IsCounselor) this.userService.SetUserCurrentRole(topRole, 'Counselor');
            else if (topRole?.IsSuccessCoach) this.userService.SetUserCurrentRole(topRole, 'SuccessCoach');
            else if (topRole?.IsStudentAdvisor) this.userService.SetUserCurrentRole(topRole, 'StudentAdvisor');
            else if (topRole?.IsDesignee) this.userService.SetUserCurrentRole(topRole, 'Designee');
            else if (topRole?.IsReader) this.userService.SetUserCurrentRole(topRole, 'Reader');
        } else {
            // No roles found. Reject politely.
            self.messageboxDialogService
                .Open(<MessageBoxDialogOptions>{
                    TitleText: 'Unable to Login',
                    MessageText: `We're sorry, we are unable to log you into the Multiple Pathways system at the current time. Although your username and password were successfully validated, the system was unable to determine a role within the Multiple Pathways system (such as school administrator, designee, program contact, etc.) for you at this time.<br /><br />We apologize for the inconvenience.`,
                })
                .subscribe((_) => {
                    self.Logout(undefined, undefined);
                });
        }
    }

    // #endregion

    // #region Refresh Authentication Token Service Method

    // RefreshAuthToken(): Observable<AuthTokenRefreshResult> {
    RefreshAuthToken(): Observable<AuthTokenRefreshResult> {
        return this.http.put<AuthTokenRefreshResult>(this.configService.hostAddress + `Api/Services/Authentication/RefreshAuthToken`, {});
    }

    // #endregion

    // #region Reset User Password Service Call

    ResetUserPassword(password: string, valtoken: string): Observable<string> {
        return this.http.get<string>(
            this.configService.hostAddress +
                `Api/Services/Authentication/ResetUserPassword?Password=${password || ''}&ValToken=${valtoken || ''}`
        );
    }

    // #endregion

    // #region Revalidate Session Service Call

    RevalidateSession(Username: string, Password: string, AuthToken: string, SAPID?: string, IsImpersonation?: boolean) {
    // RevalidateSession(Username: string, Password: string, ImpersonatedUsername: string): Observable<RevalidationInfo> {
        return this.http
            // .put<UserInfo>(this.configService.hostAddress + `api/Login/revalidate`, {
            //     Username: Username || '',
            //     Password: Password || '',
            //     ImpersonatedUsername: ImpersonatedUsername || '',
            // })
            .post<UserInfo>(this.configService.hostAddress + `Api/Services/Authentication/RevalidateSession`, {
                Username: Username || '',
                Password: Password || '',
                AuthToken: AuthToken || '',
                SAPID: SAPID || '',
                IsImpersonation: IsImpersonation || '',
            })
            .pipe(
                map((data: any) => {
                    // return {
                    //     Error: null,
                    //     UserInfo: data,
                    // } as RevalidationInfo;
                    return data;
                })
            );
    }

    // #endregion

    // #region Set System Online Status

    SetSystemOnlineStatus(status: TestConnectionPacket): void {
        this.systemOnline = status?.Status || false;
        this.SystemOnlineSubject.next(status);
    }

    // #endregion

    // #region Start AuthToken Refreshing Service Call

    StartAuthTokenRefreshing(): void {
        // This process will work as follows: We will call a service method which will tell us if the current user's auth token shows
        // "recent use" (which is a flag set by most stored procedures). If that method succeeds, and shows recent use, we will simply
        // use a different service call to update the auth token refresh time, then delay for a time period (AUTHCHECKTIME) before we
        // run this same check again.
        // If at any point the recent use flag is not set, then we set a timer to logout the user after a set amount of time
        // (LOGOUTWARNINGTIME), and simultaneously put up a dialog box that gives the user a visual representation of the countdown,
        // along with a button they can click to "save" their session, which essentially manually triggers the refresh method.

        // Initiate the auth token refresh cycling by calling the refresh method the first time
        this.AuthTokenCancelFlag = false;

        // Check real quick for a present value for AuthRefreshDeadline. If that exists, then it indicates the time when we absolutely
        // need to refresh the auth token, so we should set the refresh handle time to just before then. Otherwise, this is probably a
        // first login so use the default time.
        let timeToRefresh = AuthService.AUTHCHECKTIME;

        if (this.nextAuthRefresh) {
            // Read the deadline time to refresh from the session storage, convert into a Date object.
            this.AuthRefreshDeadline = new Date(this.nextAuthRefresh);
        } else {
            // Nothing available from session storage, so queue up a fresh refresh clock.
            this.AuthRefreshDeadline = addSeconds(new Date(), AuthService.AUTHCHECKTIME);
        }

        if (this.AuthRefreshDeadline && isValid(this.AuthRefreshDeadline)) {
            // Find the time difference in seconds between now and when we are due to refresh.
            const timediff = differenceInSeconds(this.AuthRefreshDeadline, new Date());

            // We will always use the lesser of the next known time to refresh and the usual delay time. This is in case we are refreshing
            // the browser and need to restart refresh cycle, as opposed to fresh starting cycles.
            if (timediff < timeToRefresh) {
                timeToRefresh = timediff;
            }
            if (timeToRefresh <= 0) {
                timeToRefresh = 3;
            } // Ensure at least a 3 second wait.
        }

        this.AuthTokenRefreshHandle = window.setTimeout(() => {
            this.AuthTokenRefresh();
        }, timeToRefresh * 1000);

        // Set the refresh deadline app value
        const Now = new Date();

        this.AuthRefreshDeadline = addSeconds(Now, timeToRefresh);
        this.IsRefreshing = true;
        // this.nextAuthRefresh = this.toolsService.toISOLocal(this.AuthRefreshDeadline);
        this.nextAuthRefresh = this.AuthRefreshDeadline.toISOString();
    }

    // #endregion

    // #region Test Connection Service Call

    TestConnection(): Observable<TestConnectionPacket> {
        // Call the WebApi to see if anyone is home.
        return this.http.get<TestConnectionPacket>(this.configService.hostAddress + 'Api/Services/Authentication/TestConnection');
    }

    // #endregion

    // #region Validate User

    ValidateUser(
        username: string,
        password: string,
        impersonatedUsername?: string | null,
        trainingMode?: boolean,
        screen?: string,
        winres?: string
    ): Observable<UserInfo> {
        return this.http.post<UserInfo>(this.configService.hostAddress + `Api/Services/Authentication/ValidateUser`, {
            Username: username || '',
            Password: password || '',
            TrainingMode: trainingMode || false,
            ImpersonatedUsername: impersonatedUsername || '',
            Screen: screen || '',
            Window: winres || '',
        });
    }

    // #endregion
}

export class RevalidationInfo {
    Error?: Error | null;
    UserInfo?: UserInfo | null;
}